import { useEffect, useMemo, useRef, useState } from 'react';
import { formatTimeOfDay } from '@vendorops/shared';
import { Input } from '@/components/ui/input';
import { parseSpokenTime, timeOptions } from '@/lib/time-input';
import { cn } from '@/lib/utils';

/**
 * A time field that reads what the planner types and offers the half-hours.
 *
 * The typing is the field; the menu is a shortcut. Everything the parser
 * understands — `4`, `4pm`, `615`, `18:15` — commits as it is typed, and an
 * odd time is spliced into the menu in its true place rather than treated as an
 * exception. Nothing is ever rounded to the grid: a 6:15 ceremony is a fact
 * about someone's wedding, not a value to be corrected.
 *
 * `after` makes this an end field. The menu then starts past that time, labels
 * every option with how long the day runs, and carries on through midnight,
 * which is the case two unrelated clock fields quietly get wrong — `11:00 PM`
 * to `12:30 AM` looks backwards until something says it is an hour and a half.
 *
 * The menu is a plain absolutely-positioned listbox rather than a popover.
 * A popover's dismissal rules are written for a layer you click *into*; this is
 * one field that happens to have a menu, and a click on the field itself read as
 * a click outside the layer, which opened and closed it in the same gesture.
 */

/** Where a menu with nothing chosen yet puts the keyboard, and what it scrolls
 *  to: the afternoon, because that is where weddings are. */
const START_ANCHOR = '12:00';
/** The room the menu wants. It takes less when less is available. */
const MENU_HEIGHT = 240;
/** Below this a menu is not worth opening in the direction it fits worst. */
const MIN_MENU_HEIGHT = 96;
/** Clear of the edge it opens against, so the shadow is not flush with it. */
const GUTTER = 8;

/**
 * The box that will actually cut the menu off: the window, narrowed by every
 * scrolling ancestor between here and it.
 *
 * `overflow: auto` on an ancestor clips a descendant however high its z-index
 * is, so the viewport alone is the wrong question to ask anywhere this field
 * is used inside something that scrolls.
 */
function clippingRect(from: HTMLElement | null): { top: number; bottom: number } {
  let top = 0;
  let bottom = window.innerHeight;
  for (let el = from?.parentElement ?? null; el; el = el.parentElement) {
    const overflow = getComputedStyle(el).overflowY;
    if (overflow !== 'auto' && overflow !== 'scroll' && overflow !== 'hidden') continue;
    const rect = el.getBoundingClientRect();
    top = Math.max(top, rect.top);
    bottom = Math.min(bottom, rect.bottom);
  }
  return { top, bottom };
}

export default function TimePicker({
  id,
  label,
  value,
  onChange,
  after,
  placeholder,
}: {
  id: string;
  /** The field's accessible name — the visible label above covers the pair. */
  label: string;
  /** `HH:MM`, or `''` for not set. */
  value: string;
  onChange: (next: string) => void;
  /** `HH:MM`. Present on an end field; see the header comment. */
  after?: string;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  /**
   * What is in the input while the planner is mid-entry, or null when the input
   * is simply showing `value`. Keeping the two apart is what lets `615` sit in
   * the field as typed while the form already holds `18:15`.
   */
  const [draft, setDraft] = useState<string | null>(null);
  const [activeValue, setActiveValue] = useState<string | null>(null);
  const [dropUp, setDropUp] = useState(false);
  const [menuHeight, setMenuHeight] = useState(MENU_HEIGHT);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const options = useMemo(() => timeOptions({ current: value || undefined, after }), [value, after]);

  const display = draft ?? (value ? formatTimeOfDay(value) ?? value : '');
  // Only a typed-in entry can be wrong, and only while it is still unreadable —
  // a cleared field is a legitimate answer for a wedding nobody has scheduled.
  const invalid =
    draft !== null && draft.trim() !== '' && parseSpokenTime(draft, { after }) === null;

  const anchorValue = value || (after ? options[0]?.value : START_ANCHOR) || null;
  const active = activeValue ?? anchorValue;

  /** Give the input back what the form holds, discarding an unreadable entry. */
  const settle = () => {
    setDraft(null);
    setActiveValue(null);
    setOpen(false);
  };

  // A click anywhere else is the planner moving on. Registered only while the
  // menu is up, and after the click that opened it has already been dispatched.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent | MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) settle();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  // Open away from whatever would cut the menu off, and never taller than the
  // room there actually is.
  //
  // The window is not always what clips it. Inside a scrolling container — the
  // day-of timeline draws its blocks in one — the menu is clipped by that
  // container's box however much viewport is left below, which measured
  // against `window.innerHeight` alone looked like plenty of room and opened a
  // menu that was sliced off two rows down.
  useEffect(() => {
    if (!open) return;
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const clip = clippingRect(wrapperRef.current);
    const below = clip.bottom - rect.bottom;
    const above = rect.top - clip.top;
    const up = below < MENU_HEIGHT && above > below;
    setDropUp(up);
    // Cap rather than overflow: a menu with nowhere to go still scrolls inside
    // itself, which is a menu you can use. One clipped by an ancestor is not.
    setMenuHeight(Math.max(MIN_MENU_HEIGHT, Math.min(MENU_HEIGHT, (up ? above : below) - GUTTER)));
  }, [open]);

  // Follow the keyboard down the list, and open the menu already showing the
  // part of the day in question rather than the small hours.
  //
  // The list is scrolled by hand rather than with `scrollIntoView`, which
  // scrolls every scrolling ancestor and not just the one asked (EVE-67). Used
  // inside a scrolling dialog that meant opening this menu also dragged the
  // form to the hours field, and then dragged it back from every option the
  // cursor crossed on the way down — the fields below were unreachable. The
  // menu's own scroll is the picker's to move; the form's is not.
  useEffect(() => {
    if (!open || !active) return;
    const list = listRef.current;
    const el = list?.querySelector<HTMLElement>(`[data-value="${active}"]`);
    if (!list || !el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (activeValue === null) {
      // Opening: put the anchor in the middle, so the hours either side of it
      // are in view too.
      list.scrollTop = Math.max(0, top - (list.clientHeight - el.offsetHeight) / 2);
    } else if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }, [open, active, activeValue]);

  /**
   * The form moved, so the menu goes.
   *
   * This menu is a 240px layer anchored under a field that sits in the middle
   * of a form, and it opens on focus — so until something dismissed it, it sat
   * over the fields below and took the wheel into its own option list, which is
   * the other half of EVE-67. Scrolling the form is as clear a "moving on" as
   * clicking elsewhere, so it is treated as one. Scrolling the options is not,
   * and is let through — that is the menu being used.
   *
   * Capture phase because a scroll event does not bubble past the element that
   * scrolled, and the container is an ancestor this component does not know.
   */
  useEffect(() => {
    if (!open) return;
    // Armed a frame late, like the click handler above and for the same kind of
    // reason: a field reached with Tab gets scrolled into view by the browser
    // itself, and that scroll would otherwise close the menu in the same breath
    // that opened it. Scroll steps run before animation-frame callbacks, so one
    // frame is enough to tell the browser's scroll from the planner's.
    let armed = false;
    const frame = requestAnimationFrame(() => {
      armed = true;
    });
    const onScroll = (e: Event) => {
      if (!armed) return;
      // `contains` counts the list itself, which is the case that matters: the
      // planner scrolling the options is using the menu, not leaving it.
      if (listRef.current?.contains(e.target as Node)) return;
      settle();
    };
    document.addEventListener('scroll', onScroll, true);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const commit = (next: string) => {
    onChange(next);
    setDraft(null);
    setActiveValue(null);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleType = (text: string) => {
    setDraft(text);
    setOpen(true);
    setActiveValue(null);
    const trimmed = text.trim();
    if (!trimmed) {
      onChange('');
      return;
    }
    const parsed = parseSpokenTime(trimmed, { after });
    if (parsed) onChange(parsed);
  };

  const step = (delta: number) => {
    if (!open) {
      setOpen(true);
      return;
    }
    const index = active ? options.findIndex((o) => o.value === active) : -1;
    const next = index === -1 ? 0 : Math.min(options.length - 1, Math.max(0, index + delta));
    setActiveValue(options[next]?.value ?? null);
  };

  return (
    <div ref={wrapperRef} className="relative flex-1">
      <Input
        id={id}
        ref={inputRef}
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-activedescendant={open && active ? `${id}-option-${active}` : undefined}
        aria-invalid={invalid ? true : undefined}
        aria-autocomplete="list"
        autoComplete="off"
        // No numeric inputMode: `pm` is two letters, and a phone keypad that
        // cannot type them would put the meridiem out of reach on the device
        // where scrolling the menu is hardest.
        className="px-3 tabular-nums"
        placeholder={placeholder}
        value={display}
        onChange={(e) => handleType(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          // Focus leaving the field for good; a click on an option never gets
          // here, because the option keeps the caret where it is.
          if (!wrapperRef.current?.contains(e.relatedTarget as Node)) settle();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            step(1);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            step(-1);
          } else if (e.key === 'Enter' && open && active) {
            // The menu owns Enter while it is open; the dialog's Create button
            // must not fire from under it.
            e.preventDefault();
            commit(active);
          } else if (e.key === 'Escape' && open) {
            e.preventDefault();
            settle();
          } else if (e.key === 'Tab') {
            settle();
          }
        }}
      />

      {open && (
        <div
          id={`${id}-listbox`}
          role="listbox"
          aria-label={label}
          ref={listRef}
          className={cn(
            'absolute left-0 z-50 w-full min-w-44 overflow-y-auto rounded-2xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-container-lowest)] p-1 shadow-[0_8px_28px_rgba(49,51,47,0.10)]',
            dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
          )}
          style={{ maxHeight: menuHeight }}
        >
          {options.map((option) => {
            const selected = option.value === value;
            return (
              <div
                key={option.value}
                id={`${id}-option-${option.value}`}
                data-value={option.value}
                role="option"
                aria-selected={selected}
                // Keeps the field focused through the click, so choosing from
                // the menu never blurs the entry out from under itself.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(option.value)}
                onMouseEnter={() => setActiveValue(option.value)}
                className={cn(
                  // Roomier rows on touch, where the menu carries more of the
                  // work than the keyboard does.
                  'flex cursor-pointer items-center justify-between gap-4 rounded-xl px-3 py-2.5 text-sm tabular-nums transition-colors duration-150 sm:py-2',
                  option.value === active && 'bg-[var(--color-surface-container-low)]',
                  selected && 'bg-[var(--color-primary-container)] font-medium',
                )}
              >
                <span>{option.label}</span>
                {option.detail && (
                  <span className="text-xs text-muted-foreground">{option.detail}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
